import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { PATH_TO_MODULE } from '../utils/permissions';
import ChangePasswordModal from './ChangePasswordModal';
import {
  ChartBar, Users, CurrencyDollar, CurrencyCircleDollar,
  FileText, Gear, SignOut, Buildings, UserCircle, UsersThree,
  Table, Calendar, Key, Clock, List, X, Wallet,
  ClockCounterClockwise, Bank, ClockClockwise,
} from '@phosphor-icons/react';

const MENU_ITEMS = [
  { path: '/', icon: ChartBar, label: 'Dashboard', module: 'dashboard' },
  { path: '/turnos', icon: ClockCounterClockwise, label: 'Turnos', module: 'turnos' },
  { path: '/asistencia-admin', icon: ClockClockwise, label: 'Asistencia', module: 'asistencia' },
  { path: '/costo-laboral', icon: Wallet, label: 'Costo Laboral', module: 'costo_laboral' },
  { path: '/dotacion', icon: Users, label: 'Dotacion', module: 'dotacion' },
  { path: '/planilla', icon: Table, label: 'Planilla Mensual', module: 'planilla_mensual' },
  { path: '/horas-extras', icon: Clock, label: 'Horas Extras', module: 'horas_extras' },
  { path: '/anticipos', icon: CurrencyDollar, label: 'Historial Anticipos', module: 'historial_anticipos' },
  { path: '/remuneraciones', icon: CurrencyCircleDollar, label: 'Remuneraciones', module: 'remuneraciones' },
  { path: '/trabajadores', icon: Users, label: 'Trabajadores', module: 'trabajadores', badgeKey: 'transfer' },
  { path: '/cuentas-bancarias', icon: Bank, label: 'Cuentas Bancarias', module: 'cuentas_bancarias' },
  { path: '/reportes', icon: FileText, label: 'Reportes', module: 'reportes' },
  { path: '/sucursales', icon: Buildings, label: 'Sucursales', module: 'sucursales' },
  { path: '/usuarios', icon: UsersThree, label: 'Usuarios', module: 'usuarios' },
  { path: '/mes-operativo', icon: Calendar, label: 'Mes Operativo', module: 'mes_operativo' },
  { path: '/configuracion', icon: Gear, label: 'Configuracion', module: 'configuracion' },
];

const Layout = ({ children }) => {
  const { user, logout, axiosInstance } = useAuth();
  const { canRead } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [transferCount, setTransferCount] = useState(0);

  const loadTransferCount = useCallback(async () => {
    if (!canRead('trabajadores')) return;
    try {
      const res = await axiosInstance.get('/api/workers/transfer-requests/count');
      setTransferCount(res.data.count || 0);
    } catch { /* silent */ }
  }, [axiosInstance, canRead]);

  useEffect(() => {
    loadTransferCount();
    const interval = setInterval(loadTransferCount, 30000);
    return () => clearInterval(interval);
  }, [loadTransferCount]);

  // Filter menu by permissions
  const visibleItems = MENU_ITEMS.filter(item => canRead(item.module));

  // Route guard: redirect if current path has no permission
  useEffect(() => {
    const currentModule = PATH_TO_MODULE[location.pathname];
    if (currentModule && !canRead(currentModule)) {
      const firstAllowed = visibleItems[0];
      navigate(firstAllowed ? firstAllowed.path : '/no-access', { replace: true });
    }
  }, [location.pathname, canRead, visibleItems, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleLabel = {
    admin: 'Administrador',
    jefe_local: 'Jefe de Local',
    encargado_remuneraciones: 'Enc. Remuneraciones',
    encargado_turno: 'Enc. Turno',
    encargado: 'Encargado',
  };

  // No access screen
  if (visibleItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-background)' }}>
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--people-accent-light)' }}>
            <UsersThree size={28} style={{ color: 'var(--people-primary)' }} />
          </div>
          <h2 className="font-heading text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>No tienes modulos habilitados</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Contacta a un administrador para solicitar acceso.</p>
          <button onClick={handleLogout} className="btn-primary">Cerrar sesion</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--app-background)' }}>
      {/* Sidebar */}
      <aside
        className={`min-h-screen flex flex-col transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'}`}
        style={{ background: 'linear-gradient(180deg, #7C3AED 0%, #6D28D9 100%)' }}
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className="px-5 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/tupu-logo-white.png" alt="TUPU" className="h-5" />
            <span className="font-heading font-light text-[15px] text-white/80 tracking-tight">Personas</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="p-1 text-white/50 hover:text-white transition-colors" data-testid="close-sidebar-btn">
            <X size={16} />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `sidebar-link whitespace-nowrap ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <item.icon size={19} className="shrink-0" />
              <span className="text-[13px]">{item.label}</span>
              {item.badgeKey === 'transfer' && transferCount > 0 && (
                <span className="ml-auto text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full" style={{ background: '#F59E0B' }} data-testid="transfer-badge">
                  {transferCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Area */}
        <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <UserCircle size={20} weight="fill" className="text-white/80" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-white truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-[11px] text-white/50 truncate" data-testid="user-role">{roleLabel[user?.role] || 'Encargado'}</p>
            </div>
          </div>
          <div className="space-y-0.5">
            <button onClick={() => setShowChangePassword(true)} className="flex items-center gap-2 text-[12px] text-white/60 hover:text-white transition-colors w-full py-1.5 px-1 rounded-md hover:bg-white/10 whitespace-nowrap" data-testid="change-password-btn">
              <Key size={15} className="shrink-0" /><span>Cambiar Contrasena</span>
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[12px] text-white/60 hover:text-white transition-colors w-full py-1.5 px-1 rounded-md hover:bg-white/10 whitespace-nowrap" data-testid="logout-button">
              <SignOut size={15} className="shrink-0" /><span>Cerrar sesion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        {!sidebarOpen && (
          <div className="sticky top-0 z-30 px-4 py-2.5 flex items-center gap-3 border-b" style={{ background: 'var(--people-primary)', borderColor: 'rgba(255,255,255,0.1)' }}>
            <button onClick={() => setSidebarOpen(true)} className="p-1.5 text-white hover:bg-white/10 transition-colors rounded-lg" data-testid="open-sidebar-btn">
              <List size={22} />
            </button>
            <span className="text-white font-heading font-bold text-sm">
              <img src="/tupu-logo-white.png" alt="TUPU" className="h-4 inline mr-1.5" />
              <span className="font-light opacity-80">Personas</span>
            </span>
          </div>
        )}
        <div className="p-6">{children}</div>
      </main>

      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
};

export default Layout;
